import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Calendar, Check, Edit3, ExternalLink, Loader2, Play, PowerOff, Trash2, Upload, Send } from 'lucide-react';
import { format } from 'date-fns';
import { LiveEventInlineCard } from '../components/community/LiveEventInlineCard';
import type { ChatCategory } from '../types/community';

// Data shape aligned to existing table + new columns we added
export type AdminEvent = {
  id: string;
  title: string;
  description: string | null;
  event_date: string; // ISO string
  thumbnail_url: string | null;
  event_url: string | null;
  is_started: boolean | null;
  is_ended: boolean | null;
  replay_available: boolean | null;
  created_at: string | null;
};

export default function AdminLiveEvents() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<AdminEvent[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AdminEvent | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [shareEvent, setShareEvent] = useState<AdminEvent | null>(null);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [categories, setCategories] = useState<ChatCategory[]>([]);
  const [shareCategoryId, setShareCategoryId] = useState<string | ''>('');

  const [form, setForm] = useState({
    title: '',
    description: '',
    link: '',
    date: '',
    time: '',
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setIsAdmin(false);
          return;
        }
        // Prefer profiles.is_admin gating if available
        const { data: prof } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .maybeSingle();
        const adminFlag = prof?.is_admin === true || user.email === 'markofilipovic2003@gmail.com';
        setIsAdmin(adminFlag);
        if (adminFlag) {
          await fetchEvents();
          await fetchCategories();
        }
      } catch (e) {
        console.error(e);
        setError('Failed to initialize');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from('live_events')
      .select('id, title, description, event_date, thumbnail_url, event_url, is_started, is_ended, replay_available, created_at')
      .order('event_date', { ascending: true });
    if (error) throw error;
    setEvents(data as AdminEvent[]);
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_categories')
        .select('id, name, created_at')
        .order('name');
      if (error) throw error;
      setCategories(data || []);
      // Default to the first category (likely General)
      if (data && data.length > 0) {
        setShareCategoryId(data[0].id);
      }
    } catch (e) {
      console.error('Failed to fetch categories', e);
    }
  };

  const resetForm = () => {
    setForm({ title: '', description: '', link: '', date: '', time: '' });
    setFile(null);
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (ev: AdminEvent) => {
    setEditing(ev);
    setForm({
      title: ev.title,
      description: ev.description || '',
      link: ev.event_url || '',
      date: format(new Date(ev.event_date), 'yyyy-MM-dd'),
      time: format(new Date(ev.event_date), 'HH:mm'),
    });
    setFile(null);
    setShowForm(true);
  };

  const isValidUrl = (s: string) => {
    if (!s) return false;
    try { new URL(s); return true; } catch { return false; }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }
    setFile(f);
  };

  const uploadCover = async (): Promise<string | null> => {
    if (!file) return editing?.thumbnail_url || null;
    const ext = file.name.split('.').pop();
    const name = `cover-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error: upErr } = await supabase.storage.from('event-thumbnails').upload(name, file, { upsert: false, contentType: file.type });
    if (upErr) throw upErr;
    const { data } = supabase.storage.from('event-thumbnails').getPublicUrl(name);
    return data.publicUrl;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Compose datetime
      const when = new Date(`${form.date}T${form.time}`);
      if (isNaN(when.getTime())) throw new Error('Invalid date/time');

      const payload: any = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        event_date: when.toISOString(),
        event_url: form.link.trim() || null,
        is_started: editing ? editing.is_started ?? false : false,
        is_ended: editing ? editing.is_ended ?? false : false,
        replay_available: editing ? editing.replay_available ?? false : false,
        user_id: user.id,
      };

      // Upload cover if new file selected (or require for create)
      const coverUrl = await uploadCover();
      payload.thumbnail_url = coverUrl;

      if (editing) {
        const { error } = await supabase.from('live_events').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('live_events').insert(payload);
        if (error) throw error;
      }

      await fetchEvents();
      setShowForm(false);
      resetForm();
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async (id: string) => {
    if (!window.confirm('Delete this event? This action cannot be undone.')) return;
    try {
      setDeletingId(id);
      const { error } = await supabase.from('live_events').delete().eq('id', id);
      if (error) throw error;
      await fetchEvents();
    } catch (e) {
      console.error(e);
      alert('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const startEvent = async (ev: AdminEvent) => {
    try {
      const { error } = await supabase
        .from('live_events')
        .update({ is_started: true, is_ended: false, replay_available: false, started_at: new Date().toISOString() })
        .eq('id', ev.id);
      if (error) throw error;
      await fetchEvents();
    } catch (e) {
      console.error(e);
      alert('Failed to start event');
    }
  };

  const endEvent = async (ev: AdminEvent, replay: boolean) => {
    try {
      const { error } = await supabase
        .from('live_events')
        .update({ is_ended: true, is_started: false, replay_available: replay, ended_at: new Date().toISOString() })
        .eq('id', ev.id);
      if (error) throw error;
      await fetchEvents();
    } catch (e) {
      console.error(e);
      alert('Failed to end event');
    }
  };

  const toggleReplay = async (ev: AdminEvent) => {
    try {
      const { error } = await supabase
        .from('live_events')
        .update({ replay_available: !ev.replay_available })
        .eq('id', ev.id);
      if (error) throw error;
      await fetchEvents();
    } catch (e) {
      console.error(e);
      alert('Failed to update replay');
    }
  };

  // Share selected event to community chat as a message containing [event:<id>]
  const handleShareToChat = async () => {
    if (!shareEvent) return;
    try {
      setShareSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const content = `${shareMessage?.trim() || ''}\n[event:${shareEvent.id}]`.trim();
      const { error } = await supabase
        .from('messages')
        .insert({
          content,
          user_id: user.id,
          like_count: 0,
          category_id: shareCategoryId || null,
        });
      if (error) throw error;
      // Simple toast
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 z-50 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-2 rounded-lg backdrop-blur-md shadow-lg text-sm font-medium';
      toast.textContent = 'Shared to chat!';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
      setShareEvent(null);
      setShareMessage('');
    } catch (e: any) {
      console.error(e);
      alert(e?.message || 'Failed to share to chat');
    } finally {
      setShareSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-white flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin"/> Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-white mb-2">Admin - Live Events</h1>
        <p className="text-gray-300">You are not authorized to view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Admin - Live Events</h1>
        <button onClick={openCreate} className="bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] transition-colors">Create Event</button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Share to chat Modal */}
      {shareEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShareEvent(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-gray-900 border border-white/10 rounded-xl w-[95%] max-w-2xl p-5 z-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white text-lg font-semibold">Share to chat</h2>
              <button onClick={() => setShareEvent(null)} className="text-gray-300 hover:text-white">✕</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Message</label>
                <textarea value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} rows={3} className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" placeholder="Write something about this event..." />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Category</label>
                <select value={shareCategoryId} onChange={(e) => setShareCategoryId(e.target.value)} className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]">
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Preview</label>
                <LiveEventInlineCard event={shareEvent} />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShareEvent(null)} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Cancel</button>
                <button disabled={shareSubmitting} onClick={handleShareToChat} className="px-4 py-2 bg-[#c9fffc] text-gray-900 rounded font-medium hover:bg-[#a0fcf9] disabled:opacity-50 inline-flex items-center gap-2">
                  <Send className="w-4 h-4"/> {shareSubmitting ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {events.map((ev) => {
          const isLive = !!ev.is_started && !ev.is_ended;
          const isEnded = !!ev.is_ended;
          return (
            <div key={ev.id} className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
              <div className="aspect-video bg-gray-700 relative">
                {(() => { const thumb = ev.thumbnail_url; return thumb ? (
                  <img src={thumb} alt={ev.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Calendar className="w-10 h-10"/>
                  </div>
                ); })()}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-sm">
                  <span className="text-[#c9fffc] font-medium">{format(new Date(ev.event_date), 'MMM d, yyyy h:mm a')}</span>
                </div>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">{ev.title}</h3>
                  <span className={`px-2 py-1 rounded text-xs ${isLive ? 'bg-green-500/15 text-green-300 border border-green-400/30' : isEnded ? 'bg-gray-500/15 text-gray-300 border border-gray-400/30' : 'bg-yellow-500/15 text-yellow-300 border border-yellow-400/30'}`}>
                    {isLive ? 'Live' : isEnded ? 'Ended' : 'Scheduled'}
                  </span>
                </div>
                <p className="text-gray-300 text-sm">{ev.description}</p>
                {ev.event_url && (
                  <a href={ev.event_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#c9fffc] text-sm hover:underline">
                    <ExternalLink className="w-4 h-4"/> Event Link
                  </a>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-700">
                  <button onClick={() => openEdit(ev)} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 inline-flex items-center gap-2"><Edit3 className="w-4 h-4"/> Edit</button>
                  <button onClick={() => confirmDelete(ev.id)} disabled={deletingId === ev.id} className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-500 inline-flex items-center gap-2 disabled:opacity-50"><Trash2 className="w-4 h-4"/> {deletingId === ev.id ? 'Deleting...' : 'Delete'}</button>
                  {!isLive && !isEnded && (
                    <button onClick={() => startEvent(ev)} className="px-3 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-500 inline-flex items-center gap-2"><Play className="w-4 h-4"/> Start</button>
                  )}
                  {isLive && (
                    <>
                      <button onClick={() => endEvent(ev, true)} className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500 inline-flex items-center gap-2"><PowerOff className="w-4 h-4"/> End + Replay</button>
                      <button onClick={() => endEvent(ev, false)} className="px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-500 inline-flex items-center gap-2"><PowerOff className="w-4 h-4"/> End (no replay)</button>
                    </>
                  )}
                  {isEnded && (
                    <button onClick={() => toggleReplay(ev)} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 inline-flex items-center gap-2"><Check className={`w-4 h-4 ${ev.replay_available ? 'text-emerald-400' : 'text-gray-400'}`}/> Replay {ev.replay_available ? 'On' : 'Off'}</button>
                  )}
                  <button onClick={() => { setShareEvent(ev); setShareMessage(''); }} className="px-3 py-2 bg-[#c9fffc] text-gray-900 rounded hover:bg-[#a0fcf9] inline-flex items-center gap-2"><Send className="w-4 h-4"/> Share to chat</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowForm(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-gray-900 border border-white/10 rounded-xl w-[95%] max-w-lg p-5 z-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white text-lg font-semibold">{editing ? 'Edit Event' : 'Create Event'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-300 hover:text-white">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Event Title</label>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Cover Image</label>
                <div className="flex items-center gap-3">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  <button onClick={() => fileRef.current?.click()} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 inline-flex items-center gap-2"><Upload className="w-4 h-4"/> Choose Image</button>
                  {file ? (
                    <span className="text-sm text-gray-300 truncate max-w-[200px]">{file.name}</span>
                  ) : editing?.thumbnail_url ? (
                    <span className="text-xs text-gray-400">Current image will be kept</span>
                  ) : (
                    <span className="text-xs text-gray-400">Required</span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Event Link (URL)</label>
                <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Date</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Time</label>
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full bg-gray-800 text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]" />
                </div>
              </div>
              <button disabled={submitting || !form.title || !form.date || !form.time || (!editing && !file) || !form.link || !isValidUrl(form.link)} onClick={handleSubmit} className="w-full px-4 py-2 bg-[#c9fffc] text-gray-900 rounded-lg font-medium hover:bg-[#a0fcf9] disabled:opacity-50">{submitting ? 'Saving...' : (editing ? 'Save Changes' : 'Create Event')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
